import React from 'react';
import { priceOnFinish, priceTypingProcess } from '../../supplementary_components/priceInputDelegate';
import cloneDeep from 'lodash/cloneDeep';
import { TableTemplate, isPerMileAdjustment, isRecurringBonus, perMileRowLabel, signedWeeklyRate } from './tableTemplate';
import { AddButton } from '../../general_components/content_management/reusableItems/buttons';
import { jobsTableConfigByPaymentType } from './jobsTableByPaymentTrype';
import {
    capitalizeFirstLetter,
    normalizePayoutAttachments,
    showDriverPayoutAdjustmentsAttachment,
} from '../../general_components/content_management/item_page/driver_page_components/payoutAdjustmentsSection';

const svgFile = `<svg stroke="currentColor" fill="currentColor" stroke-width="0" viewBox="0 0 512 512" class="attachment" height="1em" width="1em" style="display:inline-block;cursor:pointer;flex-shrink:0;vertical-align:middle;" xmlns="http://www.w3.org/2000/svg"><path d="M428 224H288a48 48 0 0 1-48-48V36a4 4 0 0 0-4-4h-92a64 64 0 0 0-64 64v320a64 64 0 0 0 64 64h224a64 64 0 0 0 64-64V228a4 4 0 0 0-4-4z"></path><path d="M419.22 188.59 275.41 44.78a2 2 0 0 0-3.41 1.41V176a16 16 0 0 0 16 16h129.81a2 2 0 0 0 1.41-3.41z"></path></svg>`;

const attachmentIconsHtml = (attachemnt) => {
    const icons = normalizePayoutAttachments(attachemnt).map(() => svgFile).join('');
    if (!icons) return '';
    return `<span style="display:inline-flex;align-items:center;gap:0.2rem;flex-shrink:0;margin-right:0.25rem;">${icons}</span>`;
};

const wireAdjustmentAttachmentClicks = (element, data) => {
    const attachments = normalizePayoutAttachments(data?.attachemnt);
    if (!attachments.length) return false;
    element.querySelectorAll('svg.attachment')?.forEach((svg, svgIndex) => {
        svg.style.cursor = 'pointer';
        svg.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const path = attachments[svgIndex]?.filePath;
            if (!path) return;
            showDriverPayoutAdjustmentsAttachment(path, svg);
        });
    });
    return true;
};

const columnsDoNotNeedPayoutUpdate = ["loadNumber", "origin", "destination", "endDate"];

/** Sum job payouts in integer cents so grand total matches line items (avoids float drift from e.g. 2500/7). */
function sumJobsArrayPayouts(jobsArray) {
    if (!Array.isArray(jobsArray) || jobsArray.length === 0) return 0;
    const cents = jobsArray.reduce((s, j) => s + Math.round((Number(j.payout) || 0) * 100), 0);
    return cents / 100;
}

/** Split totalCents across rows by weight using largest remainder (exact sum, no penny drift). */
function allocateCentsLargestRemainder(totalCents, weights) {
    const w = weights.map((x) => Math.max(0, Number(x) || 0));
    const sumW = w.reduce((a, b) => a + b, 0);
    if (sumW <= 0) return w.map(() => 0);
    const exact = w.map((weight) => (totalCents * weight) / sumW);
    const floors = exact.map((x) => Math.floor(x));
    const sumFloors = floors.reduce((a, b) => a + b, 0);
    let rem = totalCents - sumFloors;
    const order = exact
        .map((x, i) => ({ i, frac: x - floors[i] }))
        .sort((a, b) => (b.frac !== a.frac ? b.frac - a.frac : a.i - b.i));
    const out = [...floors];
    for (let k = 0; k < rem; k++) {
        out[order[k].i]++;
    }
    return out;
}

/** Float dollars this salary row should earn (before cent split). Layover uses current payout as target. */
function salaryRowTargetDollars(job, weekly) {
    const day = weekly / 7;
    if (job.status === 'Off Duty') return 0;
    if (job.status === 'On Duty') {
        const mult = job.shiftUtilization || 0;
        return day * mult;
    }
    if (job.status === 'Layover') {
        return Number(job.payout) || 0;
    }
    return 0;
}

function reconcileSalaryJobPayouts(statementClone) {
    const weekly = Number(statementClone.driver?.salaryPerWeek) || 0;
    const jobs = statementClone.jobsArray;
    if (!Array.isArray(jobs) || jobs.length === 0) {
        statementClone.jobsGrandTotal = 0;
        return;
    }
    const targets = jobs.map((j) => salaryRowTargetDollars(j, weekly));
    const totalTarget = targets.reduce((a, b) => a + b, 0);
    const totalCents = Math.round(totalTarget * 100);
    if (totalCents <= 0) {
        jobs.forEach((j) => { j.payout = 0; });
        statementClone.jobsGrandTotal = 0;
        return;
    }
    const allocated = allocateCentsLargestRemainder(totalCents, targets);
    jobs.forEach((j, i) => {
        j.payout = allocated[i] / 100;
    });
    statementClone.jobsGrandTotal = totalCents / 100;
}

function salaryManualEntryBlocksReconcile(job) {
    if (!job.payoutManuallyEntered) return false;
    if (job.status === 'On Duty' && Number(job.shiftUtilization) === 1) return false;
    return true;
}

function applyJobsGrandTotal(statementClone) {
    if (statementClone.paymentType === 'Salary' && !statementClone.jobsArray?.some(salaryManualEntryBlocksReconcile)) {
        reconcileSalaryJobPayouts(statementClone);
    } else {
        statementClone.jobsGrandTotal = sumJobsArrayPayouts(statementClone.jobsArray);
    }
}

function jobsTableEdited(statementClone, arrayName, updatedIndex, columnName, oldPayout){
    const job = statementClone[arrayName][updatedIndex];
    if(columnsDoNotNeedPayoutUpdate.includes(columnName)){
        return statementClone;
    }else if(columnName === "status" || columnName === "shiftUtilization"){
        const weekly = Number(statementClone.driver.salaryPerWeek) || 0;
        const oneDayCents = Math.round((weekly / 7) * 100);
        const payoutCents = Math.round((Number(job.payout) || 0) * 100);
        if(job.status === "Layover"){
            job.payout = payoutCents === oneDayCents ? 0 : roundCurrency(job.payout);
        }else{
            const raw = (weekly / 7) * (job.shiftUtilization || 0) * (job.status === "On Duty" ? 1 : 0);
            job.payout = roundCurrency(raw);
        }

    }else if(columnName === "loadRate"){
        job.payout = job.loadRate == null
            ? 0
            : roundCurrency(job.loadRate * statementClone.driver.percentageFromGross);
    }else{
        if(columnName === "share"){
            job.payoutEditable = true;
        }else if(columnName === "payout"){
            job.payoutManuallyEntered = true;
        }
        if(!job.payoutManuallyEntered){
            job.payout = roundCurrency(((job.deadhead + job.mileage) * job.perMileRate) * job.share);
        }
    }
    statementClone[arrayName][updatedIndex] = job;
    applyJobsGrandTotal(statementClone);
    return statementClone;
}
function updateDatasetUsingTag(currentStatement, arrayName, setStatementsList, tag, newVal){
    return updateDataset(currentStatement, arrayName, setStatementsList, parseInt(tag.getAttribute("data-index")), tag.getAttribute("data-name"), newVal);
}
/** Display rows are a sorted concat of adjustments + payments; map back to adjustmentsArray by identity. */
function resolveAdjustmentsIndex(currentStatement, row){
    if (!row || !currentStatement?.adjustmentsArray) return -1;
    return currentStatement.adjustmentsArray.indexOf(row);
}
function updateDataset(currentStatement, arrayName, setStatementsList, index, columnName, newVal, shiftedBy){
    if(currentStatement[arrayName][index - (shiftedBy || 0)][columnName] !== newVal){
        let statementClone = cloneDeep(currentStatement);
        statementClone[arrayName][index  - (shiftedBy || 0)][columnName] = newVal;
        switch (arrayName) {
            case "paymentsArray":
                // statementClone = paymentsArrayUpdateMethod(statementClone, arrayName, index);
                break;
            case "jobsArray":
                statementClone = jobsTableEdited(statementClone, arrayName, index, columnName, currentStatement["jobsArray"][index]["payout"]);
                break;
            default:
                break;
        }
        setStatementsList(statementClone);
        return true;
    }
    return false;
}

export function restoreCharge(currentStatement, setStatementsList, index, charge){
    let statementClone = cloneDeep(currentStatement);
    statementClone.adjustmentsArray.push(statementClone.dismissedCharges.splice(index, 1)[0]);
    statementClone.adjustmentsArray.sort((a, b) => ("weeklyRate" in b) - ("weeklyRate" in a));
    setStatementsList(statementClone);
}
export function addCharge(currentStatement, arrayName, setStatementsList, charge){
    let statementClone = cloneDeep(currentStatement);
    statementClone[arrayName].push(charge);
    setStatementsList(statementClone);
}
export function addCustomJob(currentStatement, setStatementsList, job){
    let statementClone = cloneDeep(currentStatement);
    if (!statementClone.jobsArray) statementClone.jobsArray = [];
    statementClone.jobsArray.push(job);
    applyJobsGrandTotal(statementClone);
    setStatementsList(statementClone);
}
function removeRecord (currentStatement, arrayName, setStatementsList, index, shiftedBy){
    let statementClone = cloneDeep(currentStatement);
    statementClone[arrayName].splice(index - (shiftedBy || 0), 1);
    if(arrayName === "jobsArray"){
        applyJobsGrandTotal(statementClone);
    }
    setStatementsList(statementClone);
}
function wholeMilesText(value) {
    const withoutCommas = String(value ?? '').replace(/,/g, '');
    const beforeDecimal = withoutCommas.split('.')[0];
    return beforeDecimal.replace(/\D/g, '');
}
function renderPerMileDescription(element, data, currentStatement, setCurrentStatement) {
    element.textContent = '';
    const wrap = document.createElement('div');
    wrap.className = 'perMileDescription';
    const title = document.createElement('span');
    title.className = 'perMileTitle';
    const label = perMileRowLabel(data);
    title.textContent = label;
    title.title = label;
    const input = document.createElement('input');
    input.type = 'text';
    input.inputMode = 'numeric';
    input.autocomplete = 'off';
    input.spellcheck = false;
    input.setAttribute('aria-label', 'Miles');
    const miles = Math.max(0, Math.trunc(Number(data.miles) || 0));
    input.value = String(miles);
    input.addEventListener('focus', () => input.select());
    input.addEventListener('input', () => {
        const cleaned = wholeMilesText(input.value);
        if (input.value !== cleaned) input.value = cleaned;
    });
    input.addEventListener('blur', () => {
        const cleaned = wholeMilesText(input.value);
        const nextMiles = cleaned === '' ? 0 : parseInt(cleaned, 10);
        const milesValue = Number.isFinite(nextMiles) && nextMiles > 0 ? nextMiles : 0;
        input.value = String(milesValue);
        const realIndex = resolveAdjustmentsIndex(currentStatement, data);
        if (realIndex < 0) return;
        updateDataset(currentStatement, 'adjustmentsArray', setCurrentStatement, realIndex, 'miles', milesValue, 0);
    });
    wrap.appendChild(title);
    wrap.appendChild(input);
    element.appendChild(wrap);
}
function appendAdjustmentDismiss(element, currentStatement, setCurrentStatement, data) {
    element.classList.add('hasAmountActions');
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button dismiss';
    button.onclick = () => {
        const realIndex = resolveAdjustmentsIndex(currentStatement, data);
        if (realIndex < 0) return;
        if (data.customAdjustment) {
            removeRecord(currentStatement, 'adjustmentsArray', setCurrentStatement, realIndex, 0);
        } else {
            dismissCharge(currentStatement, 'adjustmentsArray', setCurrentStatement, realIndex, 0);
        }
    };
    element.appendChild(button);
}
function dailyChargeTitle(baseTitle, daysCount) {
    if (daysCount === 7) return baseTitle || "";
    const unit = daysCount === 1 ? "day" : "days";
    return `${baseTitle || ""} (${daysCount} ${unit})`;
}
function applyDailyCalculationDays(currentStatement, setCurrentStatement, data, delta) {
    const realIndex = resolveAdjustmentsIndex(currentStatement, data);
    if (realIndex < 0) return;
    const current = currentStatement.adjustmentsArray[realIndex];
    const currentDays = Number(current.daysCount);
    const fromDays = Number.isFinite(currentDays) ? currentDays : 7;
    const nextDays = Math.max(0, Math.min(7, fromDays + delta));
    if (nextDays === fromDays) return;
    const statementClone = cloneDeep(currentStatement);
    const row = statementClone.adjustmentsArray[realIndex];
    const fullRate = Number(row.fullWeeklyRate);
    const baseTitle = row.baseTitle || row.title;
    row.daysCount = nextDays;
    row.fullWeeklyRate = Number.isFinite(fullRate) ? fullRate : (Number(row.weeklyRate) || 0);
    row.baseTitle = baseTitle;
    row.weeklyRate = roundCurrency(row.fullWeeklyRate * nextDays / 7);
    if (!row.titleManuallyEdited) {
        row.title = dailyChargeTitle(baseTitle, nextDays);
    }
    setCurrentStatement(statementClone);
}
function appendDailyDaysStepper(element, currentStatement, setCurrentStatement, data) {
    const stepper = document.createElement("div");
    stepper.className = "daysStepper";
    const minus = document.createElement("button");
    minus.type = "button";
    minus.innerText = "−";
    const days = Number.isFinite(Number(data.daysCount)) ? Number(data.daysCount) : 7;
    minus.disabled = days <= 0;
    minus.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        applyDailyCalculationDays(currentStatement, setCurrentStatement, data, -1);
    };
    const label = document.createElement("span");
    label.innerText = String(days);
    const plus = document.createElement("button");
    plus.type = "button";
    plus.innerText = "+";
    plus.disabled = days >= 7;
    plus.onclick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        applyDailyCalculationDays(currentStatement, setCurrentStatement, data, 1);
    };
    stepper.appendChild(minus);
    stepper.appendChild(label);
    stepper.appendChild(plus);
    element.appendChild(stepper);
}
function dismissCharge(currentStatement, arrayName, setStatementsList, index, shiftedBy){
    let statementClone = cloneDeep(currentStatement);
    statementClone.dismissedCharges.push(statementClone[arrayName].splice(index - (shiftedBy || 0), 1)[0]);
    setStatementsList(statementClone);
}
export function rateFormat(value){
    return priceOnFinish(value?.toFixed(2) ?? 0, false, true);
}
/** Add currency amounts without floating-point errors. Use for Net Pay etc. */
export function addCurrency(...amounts) {
    const cents = amounts.reduce((sum, a) => sum + Math.round((Number(a) || 0) * 100), 0);
    return cents / 100;
}
/** Round a value to 2 decimals for display. Use for accumulated totals. */
export function roundCurrency(value) {
    return Math.round((Number(value) || 0) * 100) / 100;
}
export function tableConfigurtationGenerator(tableName, currentStatement, setCurrentStatement, shiftedBy){
    function numericOnBlur(tag, parse, customFormat){
        const newVal = Number.isNaN(parse(tag.value.replace(/[^0-9.]/g,''))) ? 0 : parse(tag.value.replace(/[^0-9.]/g,''));
        if(!updateDatasetUsingTag(currentStatement, tableName, setCurrentStatement, tag, newVal)){
            tag.value=customFormat?customFormat(newVal):newVal;
        }
    }
    switch (tableName) {
        case "adjustmentsArray":
            return {
                headerPresent:true,
                columns:[
                    {
                        width: "20%",
                        label: "Type",
                        type: "select",
                        calculation : (val)=>{
                            return val;
                        },
                        cellEdit: (newVal, index, data) => {
                            const realIndex = resolveAdjustmentsIndex(currentStatement, data);
                            if (realIndex < 0) return;
                            const statementClone = cloneDeep(currentStatement);
                            const row = statementClone.adjustmentsArray[realIndex];
                            row.cat = newVal;
                            const amount = Number(row.amount);
                            if (newVal === "chrg" && Number.isFinite(amount) && amount > 0) {
                                row.amount = -amount;
                            }
                            setCurrentStatement(statementClone);
                        }
                    },
                    {
                        width: "60%",
                        label: "Description",
                        editable:true,
                        cellEdit: (element, data, index)=>{
                            if (isPerMileAdjustment(data)) {
                                renderPerMileDescription(element, data, currentStatement, setCurrentStatement);
                                return true;
                            }
                            const isPendingReview =
                                data["status"] === "pending" || data["status"] === "inReview";
                            if (isPendingReview) {
                                element.style.color = `var(--light-gray)`;
                            }
                            const hasAttachmentData =
                                normalizePayoutAttachments(data?.attachemnt).length > 0;
                            // Called twice by tableTemplate (before/after HTML). Wire clicks when icons exist.
                            wireAdjustmentAttachmentClicks(element, data);
                            if (hasAttachmentData) {
                                element.querySelector("input")?.addEventListener('blur', (e) => {
                                    const realIndex = resolveAdjustmentsIndex(currentStatement, data);
                                    if (realIndex < 0) return;
                                    updateDataset(currentStatement, "adjustmentsArray", setCurrentStatement, realIndex, "description", e.target.value, 0)
                                });
                            }
                            // Pending/in-review + attachment rows use calculation HTML (icons stay clickable).
                            if (isPendingReview || hasAttachmentData) {
                                return true;
                            }
                            if("loadNumber" in data){
                                return true;
                            }
                            return false;
                        },
                        calculation: (data)=>{
                            if (isPerMileAdjustment(data)) return '';
                            if("loadNumber" in data){
                                return `<a href="/loads/${data["loadId"]}" target="_blank" rel="noopener noreferrer">
                                Load# ${data["loadNumber"]} </a> - ${data["title"]}`;
                            }
                            if("id" in data && data["attachemnt"]){
                                const attachmentIcons = attachmentIconsHtml(data["attachemnt"]);
                                if(data["status"] === "pending" || data["status"] === "inReview"){
                                    return `${attachmentIcons}${data['description']} (${capitalizeFirstLetter(data['type'])}) `
                                }else{
                                    return `<div style="display:flex;align-items:center;gap:0.25rem;min-width:0;">${attachmentIcons}<input style="min-width:0;flex:1;" value="${data['description']} ${data['description'] === 'No description' ? '(' + capitalizeFirstLetter(data["type"]) +')' : '' }" /></div>`;
                                }
                                
                                // return `${input} (${capitalizeFirstLetter(data["type"])}${data["attachemnt"] ? svgFile : ""})`;
                            }
                            return data["title"] || data["description"];
                        },
                        onBlur: (tag, data)=>{
                            const realIndex = resolveAdjustmentsIndex(currentStatement, data);
                            if (realIndex < 0) return;
                            if (data.allowDailyCalculation && tag.value !== data.title) {
                                const statementClone = cloneDeep(currentStatement);
                                statementClone.adjustmentsArray[realIndex].title = tag.value;
                                statementClone.adjustmentsArray[realIndex].titleManuallyEdited = true;
                                setCurrentStatement(statementClone);
                                return;
                            }
                            updateDataset(currentStatement, "adjustmentsArray", setCurrentStatement, realIndex, "title", tag.value, 0)
                        },
                    },
                    {
                        width: "20%",
                        label: "Amount",
                        alignment: "right",
                        customFormat: rateFormat,
                        editInputStyle: (input)=>{
                            input.style.textAlign = "right";
                        },
                        cellEdit: (element, data, index)=>{
                            if (isPerMileAdjustment(data)) {
                                appendAdjustmentDismiss(element, currentStatement, setCurrentStatement, data);
                                return true;
                            }
                            if(data["status"] === "pending" || data["status"] === "inReview"){
                                element.style.color = `var(--light-gray)`
                                let button = document.createElement("button");
                                button.className = "button accept";
                                button.innerText="Approve";
                                button.onclick = () => {
                                    const realIndex = resolveAdjustmentsIndex(currentStatement, data);
                                    if (realIndex < 0) return;
                                    updateDataset(currentStatement, "adjustmentsArray", setCurrentStatement, realIndex, "status", "approved", 0);
                                };
                                element.appendChild(button);
                                button = document.createElement("button");
                                button.className = "button decline";
                                button.innerText="Dismiss";
                                button.onclick = () => {
                                    const realIndex = resolveAdjustmentsIndex(currentStatement, data);
                                    if (realIndex < 0) return;
                                    dismissCharge(currentStatement, "adjustmentsArray", setCurrentStatement, realIndex, 0);
                                };
                                element.appendChild(button);
                                return true;
                            }else if(!("paidAmount" in data) || "notEditable" in data){
                                element.classList.add("hasAmountActions");
                                let button = document.createElement("button");
                                button.className = "button dismiss";
                                button.onclick = () => {
                                    const realIndex = resolveAdjustmentsIndex(currentStatement, data);
                                    if (realIndex < 0) return;
                                    if(data['customAdjustment']){
                                        removeRecord(currentStatement, "adjustmentsArray", setCurrentStatement, realIndex, 0);
                                    }else{
                                        dismissCharge(currentStatement, "adjustmentsArray", setCurrentStatement, realIndex, 0);
                                    }
                                    
                                };
                                element.appendChild(button);
                                if (data.allowDailyCalculation) {
                                    appendDailyDaysStepper(element, currentStatement, setCurrentStatement, data);
                                }
                                return "notEditable" in data ? true : false;
                            }
                            return true;
                        },
                        editable:true,
                        onEdit: (val)=>{
                            if(val !== '-' && val !== '-$' && val !== '-$0' && val !== '-$0.00'){
                                return priceTypingProcess(val, true)
                            }
                            return val;
                        },
                        onBlur: (tag, data)=>{
                            let newVal = data.customAdjustment ? tag.value.replace(/(?!^-)[^0-9.]/g, ''):tag.value.replace(/[^0-9.]/g,'');
                            if(Number.isNaN(parseFloat(newVal)))newVal="0";
                            let numeric = parseFloat(newVal);
                            const amountKey = "amount" in data ? "amount" : "weeklyRate";
                            if (data.customAdjustment && data.cat === "chrg" && amountKey === "amount" && numeric > 0) {
                                numeric = -numeric;
                            }
                            const realIndex = resolveAdjustmentsIndex(currentStatement, data);
                            if (realIndex < 0) {
                                // Payment row merged into the display list
                                const payIndex = currentStatement.paymentsArray?.indexOf(data) ?? -1;
                                if (payIndex < 0 || updateDataset(currentStatement, "paymentsArray", setCurrentStatement, payIndex, "weeklyRate", parseFloat(newVal), 0)) {
                                    return;
                                }
                                let fommatted = rateFormat(parseFloat(newVal));
                                tag.value = "-"+fommatted;
                                return;
                            }
                            if(!updateDataset(currentStatement, "adjustmentsArray", setCurrentStatement, realIndex, amountKey, numeric, 0)){
                                let fommatted = rateFormat(numeric);
                                const showAsCharge = ("weeklyRate" in data && !isRecurringBonus(data)) || data["type"] === "charge";
                                tag.value = showAsCharge && numeric >= 0 ? "-"+fommatted : fommatted;
                            }
                        },
                        onPaste: priceOnFinish,
                        calculation: (data)=>{
                            if("weeklyRate" in data){
                                return signedWeeklyRate(data);
                            }
                            return data["type"] === "charge" ? -data["amount"] : data["amount"];
                        }
                    },
                ]
            }
        case "paymentsArray":
            return {
                headerPresent:true,
                columns:[
                    {
                        width: "40%",
                        label: "Description",
                        name: "title",
                    },
                    {
                        width: "20%",
                        label: "Total Amount",
                        customFormat: rateFormat,
                        calculation: (data)=>{
                            return data.totalAmount;
                        }
                    },
                    {
                        width: "20%",
                        label: "This Week's Charge",
                        name: "weeklyRate",
                        customFormat: rateFormat,
                        editable:true,
                        onEdit: priceTypingProcess,
                        onBlur: (tag)=>{
                            numericOnBlur(tag, parseFloat, rateFormat);
                        },
                        onPaste: priceOnFinish
                    },
                    {
                        width: "20%",
                        label: "New Balance",
                        alignment: "right",
                        customFormat: rateFormat,
                        calculation: (data)=>{
                            return data.weeklyRate + data.paidAmount - data.totalAmount;
                        }
                    },
                ]
            }
        case "jobsArray" :
            return jobsTableConfigByPaymentType(currentStatement.paymentType, numericOnBlur, rateFormat, removeRecord, currentStatement, setCurrentStatement, updateDataset);
        default:
            break;
    }
}
export const CustomTable = ({currentStatement, customDataset, setCurrentStatement, tableName, tableSummaryConf, shiftedBy, addButton, isEditable}) =>{
    const tableConf = tableConfigurtationGenerator(tableName, currentStatement, setCurrentStatement, shiftedBy);
    const handleReorder = (newOrder) => {
        const clone = cloneDeep(currentStatement);
        clone[tableName] = newOrder;
        setCurrentStatement(clone);
    };
    return <>
        <TableTemplate tableConf={tableConf} currentDataset={customDataset || currentStatement[tableName]} isEditable={isEditable} onReorder={tableConf.reorderingAllowed ? handleReorder : undefined} />
        {tableSummaryConf ? <TableTemplate tableConf={tableSummaryConf} currentDataset={[1]} /> : null}
        {addButton? <div style={{marginTop:"0.75rem"}}>{isEditable ? <AddButton  action={addButton} customSize={"1.5rem"} /> : null}</div>  :null}
    </>
}
// action, inactive, customSize